<template>
  <!-- Vista previa borrosa mientras carga: la silueta de lo que se va a dibujar,
       en vez de spinners y textos sueltos. Una sola forma de cargar en toda la app. -->
  <div
    class="gt-skel"
    :class="['gt-skel--' + variant, { 'gt-skel--padded': padded }]"
    role="status"
    aria-busy="true"
    :aria-label="label || $t('LoadingData')"
  >
    <!-- Listado completo: barra, pestañas y tabla. -->
    <template v-if="variant === 'page'">
      <div class="gt-skel-toolbar">
        <span class="gt-skel-bar gt-skel-bar--title"></span>
        <span class="gt-skel-bar gt-skel-bar--input"></span>
        <span class="gt-skel-bar gt-skel-bar--btn"></span>
        <span class="gt-skel-bar gt-skel-bar--btn"></span>
      </div>
      <div class="gt-skel-tabs">
        <span
          v-for="t in 4"
          :key="'tab-' + t"
          class="gt-skel-bar gt-skel-bar--tab"
        ></span>
      </div>
    </template>

    <!-- Tabla (también el final de «page»). -->
    <table
      v-if="variant === 'page' || variant === 'table'"
      class="table table-bordered mb-0 gt-skel-table"
    >
      <thead>
        <tr>
          <th v-for="c in nCols" :key="'h-' + c">
            <span class="gt-skel-bar" :style="barStyle(0, c, 45)"></span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="r in nRows" :key="'r-' + r">
          <td v-for="c in nCols" :key="'c-' + r + '-' + c">
            <span class="gt-skel-bar" :style="barStyle(r, c)"></span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Tarjetas (listados en teléfono, grillas de tarjetas). -->
    <template v-else-if="variant === 'cards'">
      <div v-for="r in nRows" :key="'card-' + r" class="gt-skel-card">
        <span
          class="gt-skel-bar gt-skel-bar--card-title"
          :style="barStyle(r, 0, 45)"
        ></span>
        <div
          v-for="l in 3"
          :key="'line-' + r + '-' + l"
          class="gt-skel-card__line"
        >
          <span class="gt-skel-bar" style="width: 28%"></span>
          <span class="gt-skel-bar" :style="barStyle(r, l, 30)"></span>
        </div>
      </div>
    </template>

    <!-- Formulario: etiqueta + campo, en dos columnas. -->
    <template v-else-if="variant === 'form'">
      <span class="gt-skel-bar gt-skel-bar--title gt-skel-mb"></span>
      <div class="gt-skel-grid">
        <div v-for="r in nRows" :key="'f-' + r" class="gt-skel-field">
          <span class="gt-skel-bar gt-skel-bar--label" :style="barStyle(r, 1, 20)"></span>
          <span class="gt-skel-bar gt-skel-bar--input-full"></span>
        </div>
      </div>
    </template>

    <!-- Detalle de un registro: encabezado, pares dato/valor y una sección. -->
    <template v-else-if="variant === 'detail'">
      <div class="gt-skel-toolbar">
        <span class="gt-skel-bar gt-skel-bar--title"></span>
        <span class="gt-skel-bar gt-skel-bar--btn"></span>
      </div>
      <div class="gt-skel-grid gt-skel-mb">
        <div v-for="r in nRows" :key="'d-' + r" class="gt-skel-kv">
          <span class="gt-skel-bar" style="width: 35%"></span>
          <span class="gt-skel-bar" :style="barStyle(r, 2, 30)"></span>
        </div>
      </div>
      <div class="gt-skel-block"></div>
    </template>

    <!-- Tablero: tarjetas de números y dos gráficos. -->
    <template v-else-if="variant === 'dashboard'">
      <div class="gt-skel-stats">
        <div v-for="s in nCols" :key="'s-' + s" class="gt-skel-stat">
          <span class="gt-skel-bar" style="width: 45%"></span>
          <span class="gt-skel-bar gt-skel-bar--number"></span>
        </div>
      </div>
      <div class="gt-skel-charts">
        <div class="gt-skel-block"></div>
        <div class="gt-skel-block"></div>
      </div>
    </template>

    <!-- Lista de ítems: ícono redondo + título y bajada (tickets, chats, emails). -->
    <template v-else-if="variant === 'list'">
      <div v-for="r in nRows" :key="'l-' + r" class="gt-skel-item">
        <span class="gt-skel-dot"></span>
        <div class="gt-skel-item__text">
          <span class="gt-skel-bar" :style="barStyle(r, 1, 30)"></span>
          <span class="gt-skel-bar gt-skel-bar--thin" :style="barStyle(r, 3, 45)"></span>
        </div>
      </div>
    </template>

    <!-- Formulario de un registro: título y grupos plegados (ícono + nombre),
         con Guardar / Cancelar al pie. Es lo que dibuja Form.vue al abrir. -->
    <template v-else-if="variant === 'accordion'">
      <div v-if="withTitle" class="gt-skel-toolbar gt-skel-mb">
        <span class="gt-skel-bar gt-skel-bar--heading"></span>
        <span class="gt-skel-bar gt-skel-bar--subheading"></span>
      </div>
      <div class="gt-skel-panel">
        <div v-for="r in nRows" :key="'g-' + r" class="gt-skel-group">
          <span class="gt-skel-dot gt-skel-dot--sm"></span>
          <span
            class="gt-skel-bar gt-skel-bar--group"
            :style="{ width: groupWidth(r) }"
          ></span>
        </div>
        <div class="gt-skel-actions">
          <span class="gt-skel-bar gt-skel-bar--save"></span>
          <span class="gt-skel-bar gt-skel-bar--link"></span>
        </div>
      </div>
    </template>

    <!-- Calendario mensual: mes, leyenda y grilla de 7 columnas con etiquetas. -->
    <template v-else-if="variant === 'calendar'">
      <div class="gt-skel-cal-head">
        <span class="gt-skel-bar gt-skel-bar--nav"></span>
        <span class="gt-skel-bar gt-skel-bar--heading"></span>
        <span class="gt-skel-bar gt-skel-bar--nav"></span>
      </div>
      <div class="gt-skel-cal-legend">
        <span class="gt-skel-pill-dot gt-skel-tone-a"></span>
        <span class="gt-skel-bar" style="width: 5rem"></span>
        <span class="gt-skel-pill-dot gt-skel-tone-b"></span>
        <span class="gt-skel-bar" style="width: 7rem"></span>
      </div>
      <div class="gt-skel-cal">
        <div v-for="d in 7" :key="'wd-' + d" class="gt-skel-cal__wd">
          <span class="gt-skel-bar" style="width: 2.2rem"></span>
        </div>
        <div v-for="c in 35" :key="'cd-' + c" class="gt-skel-cal__day">
          <span class="gt-skel-bar gt-skel-bar--daynum"></span>
          <span
            v-for="e in calendarEvents(c)"
            :key="'ev-' + c + '-' + e"
            class="gt-skel-pill"
            :class="(c + e) % 4 === 0 ? 'gt-skel-tone-a' : 'gt-skel-tone-b'"
          ></span>
        </div>
      </div>
    </template>

    <!-- Bloque grande: un gráfico, un mapa, una vista previa. -->
    <template v-else-if="variant === 'block'">
      <div class="gt-skel-block" :style="{ height: height || '16rem' }"></div>
    </template>

    <!-- Texto: renglones (cuerpos de modal, secciones chicas). -->
    <template v-else>
      <div v-for="r in nRows" :key="'t-' + r" class="gt-skel-line">
        <span
          class="gt-skel-bar"
          :style="{ width: r === nRows ? '45%' : barStyle(r, 1, 55).width }"
        ></span>
      </div>
    </template>
  </div>
</template>

<script>
/** Ancho de la barra: varía por fila y columna para que parezca texto real,
 *  pero es determinístico (no salta entre renders). */
export function skeletonBarWidth(row, col, base = 35) {
  return Math.min(base + ((row * 7 + col * 13) % 45), 100) + "%";
}

const DEFAULT_ROWS = {
  page: 8,
  table: 6,
  cards: 4,
  form: 8,
  accordion: 10,
  detail: 8,
  list: 5,
  lines: 4,
};

export default {
  name: "ContentSkeleton",
  props: {
    /** page | table | cards | form | accordion | detail | dashboard | list |
     *  calendar | block | lines */
    variant: { type: String, default: "lines" },
    /** Filas / campos / ítems / renglones; cada forma tiene su valor por defecto. */
    rows: { type: Number, default: null },
    /** Columnas de la tabla, o tarjetas de números en «dashboard». */
    cols: { type: Number, default: null },
    /** Alto de «block» (CSS). */
    height: { type: String, default: "" },
    /** Aire alrededor, para cuando reemplaza un spinner centrado con padding. */
    padded: { type: Boolean, default: false },
    /** accordion: dibujar también el título de la pantalla («Editar …»). */
    withTitle: { type: Boolean, default: true },
    label: { type: String, default: "" },
  },
  computed: {
    nRows() {
      return this.rows || DEFAULT_ROWS[this.variant] || 4;
    },
    nCols() {
      return this.cols || (this.variant === "dashboard" ? 4 : 6);
    },
  },
  methods: {
    barStyle(row, col, base) {
      return { width: skeletonBarWidth(row, col, base) };
    },
    /** Nombres de grupo: cortos y largos, como «General» o «Proforma Supplier (PS)». */
    groupWidth(row) {
      return [7, 16, 8, 12, 11, 20, 10, 12, 15, 9, 11][row % 11] + "rem";
    },
    /** Etiquetas por día: 0 a 4, repartidas como en un mes real. */
    calendarEvents(cell) {
      return [1, 0, 2, 0, 1, 3, 0, 4, 2, 0, 1, 2, 0, 1][(cell * 5) % 14];
    },
  },
};
</script>

<style>
/* Sin scope: el listado dinámico usa las mismas barras dentro de su propia tabla.
   El color sale de currentColor, así sigue al tema (claro / dark.css). */
.gt-skel {
  width: 100%;
}

.gt-skel--padded {
  padding: 1rem;
}

.gt-skel-bar {
  display: inline-block;
  vertical-align: middle;
  height: 0.75rem;
  width: 60%;
  max-width: 100%;
  border-radius: 999px;
  background-color: currentColor;
  opacity: 0.08;
  filter: blur(2px);
  animation: gt-skel-pulse 1.6s ease-in-out infinite;
}

.gt-skel-block,
.gt-skel-dot {
  background-color: currentColor;
  opacity: 0.07;
  filter: blur(3px);
  animation: gt-skel-pulse 1.6s ease-in-out infinite;
}

.gt-skel-row:nth-child(even) .gt-skel-bar,
.gt-skel tbody tr:nth-child(even) .gt-skel-bar,
.gt-skel-item:nth-child(even) .gt-skel-bar,
.gt-skel-field:nth-child(even) .gt-skel-bar {
  animation-delay: 0.2s;
}

@keyframes gt-skel-pulse {
  0%,
  100% {
    opacity: 0.06;
  }
  50% {
    opacity: 0.13;
  }
}

/* El contenido real aparece con un fundido corto, no de golpe. */
.gt-list-fade-in {
  animation: gt-list-fade-in 0.25s ease-out;
}

@keyframes gt-list-fade-in {
  from {
    opacity: 0;
    filter: blur(3px);
  }
  to {
    opacity: 1;
    filter: none;
  }
}

.gt-skel-table td,
.gt-skel-table th,
.gt-skel-tbody td {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}

.gt-skel-tbody td,
.gt-skel-table td,
.gt-skel-table th {
  border-color: rgba(127, 127, 127, 0.12) !important;
}

.gt-skel-mb {
  margin-bottom: 1rem;
}

.gt-skel-toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
}

.gt-skel-tabs {
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem 0 1rem;
  overflow: hidden;
}

.gt-skel-bar--title {
  height: 1.4rem;
  width: 9rem;
}

.gt-skel-bar--input {
  height: 2rem;
  width: 14rem;
  border-radius: 0.5rem;
}

.gt-skel-bar--input-full {
  height: 2.2rem;
  width: 100%;
  border-radius: 0.5rem;
}

.gt-skel-bar--btn {
  height: 2rem;
  width: 7rem;
  border-radius: 0.5rem;
}

.gt-skel-bar--tab {
  height: 2.2rem;
  width: 11rem;
  flex-shrink: 0;
  border-radius: 0.5rem;
}

.gt-skel-bar--label {
  height: 0.6rem;
}

.gt-skel-bar--thin {
  height: 0.55rem;
}

.gt-skel-bar--number {
  height: 1.6rem;
  width: 65%;
  border-radius: 0.5rem;
}

.gt-skel-card {
  padding: 0.9rem 1rem;
  margin-bottom: 0.6rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(127, 127, 127, 0.12);
}

.gt-skel-bar--card-title {
  height: 1rem;
  margin-bottom: 0.75rem;
}

.gt-skel-card__line,
.gt-skel-kv {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.3rem 0;
}

.gt-skel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: 1rem 1.5rem;
}

.gt-skel-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.gt-skel-block {
  height: 14rem;
  border-radius: 0.75rem;
}

.gt-skel-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.gt-skel-stat {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid rgba(127, 127, 127, 0.12);
}

.gt-skel-charts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
  gap: 1rem;
}

.gt-skel-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0;
}

.gt-skel-dot {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
}

.gt-skel-item__text {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  flex: 1;
  min-width: 0;
}

.gt-skel-line {
  padding: 0.35rem 0;
}

.gt-skel-bar--heading {
  height: 1.8rem;
  width: 14rem;
  border-radius: 0.5rem;
}

.gt-skel-bar--subheading {
  height: 1rem;
  width: 9rem;
}

.gt-skel-panel {
  padding: 1.25rem 1.5rem 1.5rem;
  border-radius: 1rem;
  border: 1px solid rgba(127, 127, 127, 0.14);
}

.gt-skel-group {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  height: 3.8rem;
  border-bottom: 1px solid rgba(127, 127, 127, 0.14);
}

.gt-skel-dot--sm {
  width: 1.5rem;
  height: 1.5rem;
}

.gt-skel-bar--group {
  height: 1rem;
  max-width: 70%;
}

.gt-skel-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding-top: 2.5rem;
}

.gt-skel-bar--save {
  height: 3rem;
  width: 7.5rem;
  border-radius: 0.6rem;
}

.gt-skel-bar--link {
  height: 1rem;
  width: 5rem;
}

.gt-skel-cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.gt-skel-bar--nav {
  height: 2.4rem;
  width: 3.6rem;
  border-radius: 0.6rem;
}

.gt-skel-cal-legend {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.gt-skel-cal-legend .gt-skel-bar {
  margin-right: 1rem;
}

.gt-skel-cal {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border: 1px solid rgba(127, 127, 127, 0.14);
  border-radius: 0.75rem;
  overflow: hidden;
}

.gt-skel-cal__wd {
  display: flex;
  justify-content: center;
  padding: 1.1rem 0;
  border-bottom: 1px solid rgba(127, 127, 127, 0.14);
}

.gt-skel-cal__day {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-height: 9rem;
  padding: 0.75rem 0.6rem;
  border-right: 1px solid rgba(127, 127, 127, 0.1);
  border-bottom: 1px solid rgba(127, 127, 127, 0.1);
  min-width: 0;
}

.gt-skel-bar--daynum {
  width: 1.2rem;
  height: 1rem;
  margin-bottom: 0.4rem;
}

/* Etiquetas de evento: el color del sistema, apagado y borroso. */
.gt-skel-pill,
.gt-skel-pill-dot {
  opacity: 0.22;
  filter: blur(2px);
  animation: gt-skel-pulse-strong 1.6s ease-in-out infinite;
}

.gt-skel-pill {
  display: block;
  height: 2rem;
  border-radius: 0.4rem;
}

.gt-skel-pill-dot {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 50%;
}

.gt-skel-tone-a {
  background-color: var(--bs-primary, #0d6efd);
}

.gt-skel-tone-b {
  background-color: var(--bs-danger, #dc3545);
}

@keyframes gt-skel-pulse-strong {
  0%,
  100% {
    opacity: 0.16;
  }
  50% {
    opacity: 0.3;
  }
}

@media (max-width: 767.98px) {
  .gt-skel-cal__day {
    min-height: 4.5rem;
    padding: 0.4rem 0.25rem;
  }

  .gt-skel-pill {
    height: 0.9rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .gt-skel-pill,
  .gt-skel-pill-dot,
  .gt-skel-bar,
  .gt-skel-block,
  .gt-skel-dot,
  .gt-list-fade-in {
    animation: none;
  }
}
</style>
